import { scheduleAPI } from '../api/schedule';
import { blockedDatesAPI } from '../api/blockedDates';
import { appointmentsAPI } from '../api/appointments';
import { getClinicWorkingDays, getClinicBlockedDates, getClinicAppointments } from '../api/clinic';
import { isDesktopRuntime, setScheduleCache, getScheduleCache } from './db';

/**
 * Read-cache refresh for scheduling, mirroring offline/patientSync.ts.
 * Solo-professional and clinic scheduling are separate backend systems
 * (see backend/apps/scheduling vs backend/apps/clinics), so they're
 * cached under separate keys rather than unified into one shape.
 */

export function soloCacheKeys(professionalId: number) {
  return {
    workingDays: `solo:workingDays:${professionalId}`,
    blockedDates: `solo:blockedDates:${professionalId}`,
    appointments: `solo:appointments:${professionalId}`,
  };
}

export function clinicCacheKeys(clinicId: number, professionalId: number) {
  return {
    workingDays: `clinic:${clinicId}:workingDays:${professionalId}`,
    blockedDates: `clinic:${clinicId}:blockedDates:${professionalId}`,
    appointments: `clinic:${clinicId}:appointments:${professionalId}`,
  };
}

export async function pullSoloScheduleIntoCache(professionalId: number): Promise<void> {
  if (!isDesktopRuntime()) return;
  const keys = soloCacheKeys(professionalId);

  const [workingDaysRes, blockedDatesRes, appointmentsRes] = await Promise.all([
    scheduleAPI.getWorkingDays(professionalId),
    blockedDatesAPI.getByProfessional(professionalId),
    appointmentsAPI.getAll({ professional: professionalId }),
  ]);

  await Promise.all([
    setScheduleCache(keys.workingDays, workingDaysRes.data.results),
    setScheduleCache(keys.blockedDates, blockedDatesRes.data.results),
    setScheduleCache(keys.appointments, appointmentsRes.data.results),
  ]);
}

export async function pullClinicScheduleIntoCache(
  clinicId: number,
  professionalId: number
): Promise<void> {
  if (!isDesktopRuntime()) return;
  const keys = clinicCacheKeys(clinicId, professionalId);

  const [workingDaysRes, blockedDatesRes, appointmentsRes] = await Promise.all([
    getClinicWorkingDays(clinicId, professionalId),
    getClinicBlockedDates(clinicId, professionalId),
    getClinicAppointments(clinicId, { professionalId }),
  ]);

  await Promise.all([
    setScheduleCache(keys.workingDays, workingDaysRes.results),
    setScheduleCache(keys.blockedDates, blockedDatesRes.results),
    setScheduleCache(keys.appointments, appointmentsRes.results),
  ]);
}

export async function getCachedSoloSchedule(professionalId: number) {
  const keys = soloCacheKeys(professionalId);
  const [workingDays, blockedDates, appointments] = await Promise.all([
    getScheduleCache(keys.workingDays),
    getScheduleCache(keys.blockedDates),
    getScheduleCache(keys.appointments),
  ]);
  return {
    workingDays: (workingDays as any[]) ?? [],
    blockedDates: (blockedDates as any[]) ?? [],
    appointments: (appointments as any[]) ?? [],
  };
}

export async function getCachedClinicSchedule(clinicId: number, professionalId: number) {
  const keys = clinicCacheKeys(clinicId, professionalId);
  const [workingDays, blockedDates, appointments] = await Promise.all([
    getScheduleCache(keys.workingDays),
    getScheduleCache(keys.blockedDates),
    getScheduleCache(keys.appointments),
  ]);
  return {
    workingDays: (workingDays as any[]) ?? [],
    blockedDates: (blockedDates as any[]) ?? [],
    appointments: (appointments as any[]) ?? [],
  };
}
